import {
  accessOf,
  ONBOARDING_SUPPLIERS,
  onboardingSteps,
  type Company,
  type OnboardingFacts,
  type OnboardingStepId,
  t,
} from '@procumi/shared';
import { useState } from 'react';
import { useAsync } from '../hooks/useAsync';
import type { Toast } from '../hooks/useToast';
import { api, errMsg } from '../lib/api';
import type { Route } from '../routes';

/**
 * Ce scrie la fiecare pas și unde te duce: direct în formularul pasului, nu în lista de unde
 * l-ai mai căuta o dată (Ticket 08, P4).
 */
const STEP: Record<
  OnboardingStepId,
  { title: string; hint: () => string; to: (f: OnboardingFacts) => Route }
> = {
  site: {
    title: t('Adaugă primul șantier'),
    hint: () => t('Cererile, comenzile și bugetul se țin pe șantiere.'),
    to: () => ({ view: 'sites', create: true }),
  },
  suppliers: {
    title: t('Adaugă cel puțin {0} furnizori', ONBOARDING_SUPPLIERS),
    hint: () => t('Îi lipești dintr-un tabel Excel sau îi adaugi pe rând, cu numele și emailul lor.'),
    to: () => ({ view: 'suppliers', import: true }),
  },
  rfq: {
    title: t('Fă prima cerere de ofertă'),
    hint: () => t('Procumi trimite câte un mail fiecărui furnizor, în numele tău.'),
    to: () => ({ view: 'new' }),
  },
  compare: {
    title: t('Compară ofertele și alege furnizorul'),
    hint: () =>
      t('Ofertele vin pe mail sau prin link și le vezi una lângă alta, cu transportul și termenele.'),
    to: (f) => (f.compareRfqId ? { view: 'rfq', id: f.compareRfqId } : { view: 'rfqs' }),
  },
  team: {
    title: t('Invită un coleg'),
    hint: () => t('Șeful de șantier sau cine mai cumpără lucrează în același cont.'),
    to: () => ({ view: 'company', tab: 'team' }),
  },
};

/**
 * „Primii pași”, pe panou, pentru firmele noi. Pașii se bifează singuri din ce
 * există în cont; lista dispare când sunt toți gata sau când o ascunzi. Firmele
 * de dinainte de pachete nu o văd: au lucrat deja fără ea.
 */
export function FirstSteps({
  company,
  go,
  toast,
  onHidden,
}: {
  company: Company;
  go: (r: Route) => void;
  toast: Toast;
  onHidden: (c: Company) => void;
}) {
  const access = accessOf(company);
  const show = !company.onboardingHidden && access.state !== 'legacy' && access.writable;
  const { data } = useAsync(
    () => (show ? api.onboarding() : Promise.resolve(null)),
    [show],
    show ? 'onboarding' : undefined,
  );
  const [busy, setBusy] = useState(false);

  if (!show || !data) return null;
  const steps = onboardingSteps(data);
  const done = steps.filter((s) => s.done).length;
  if (done === steps.length) return null;
  const next = steps.find((s) => !s.done)!.id;

  const hide = async () => {
    setBusy(true);
    try {
      onHidden(await api.hideOnboarding());
    } catch (e) {
      toast(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <section className="sheet first-steps" aria-labelledby="first-steps-t">
      <div className="fs-head">
        <div>
          <h2 id="first-steps-t">{t('Primii pași')}</h2>
          <p className="hint">
            {t('{0} din {1} gata. Se bifează singuri, pe măsură ce lucrezi.', done, steps.length)}
          </p>
        </div>
        <button className="auth-link fs-hide" type="button" disabled={busy} onClick={() => void hide()}>
          {t('Ascunde lista')}
        </button>
      </div>
      <div className="fs-bar" aria-hidden="true">
        <span style={{ width: `${(done / steps.length) * 100}%` }} />
      </div>
      <ol className="fs-list">
        {steps.map((s) => {
          const step = STEP[s.id];
          return (
            <li key={s.id} className={s.done ? 'done' : s.id === next ? 'next' : undefined}>
              <span className="fs-mark" aria-hidden="true" />
              <div className="fs-text">
                <b>
                  {t(step.title)}
                  {s.done ? <span className="sr-only"> {t('(gata)')}</span> : null}
                </b>
                {s.done ? null : <span>{step.hint()}</span>}
              </div>
              {s.done ? null : (
                <button
                  className={`btn small${s.id === next ? ' primary' : ''}`}
                  type="button"
                  onClick={() => go(step.to(data))}
                >
                  {s.id === next ? t('Începe') : t('Deschide')}
                </button>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
